/**
 * Configuration des familles de navigation.
 * Fichier pur (pas de composant React) — import sûr partout.
 * Source de vérité : URL → famille active, familles → sidebar items.
 */

import { ProfilEnum } from '@/shared/types/user';
import { PLUGIN_PAGES } from '@/shared/plugins';

// ── Types ────────────────────────────────────────────────────────────────────

export type FamilyId = 'clinique' | 'administration' | 'supervision' | 'integrations';

export interface SidebarItem {
  path: string;
  label: string;
  icon: string;
  profils: string[] | null;
}

export interface FamilyMeta {
  id: FamilyId;
  label: string;
  icon: string;
  landing: string;
  profils: string[] | null;
}

// ── URL prefixes par famille ──────────────────────────────────────────────────

export const FAMILY_PATHS: Record<FamilyId, string[]> = {
  clinique:       ['/home', '/patients', '/clinique/'],
  administration: ['/admin/users', '/admin/patients', '/admin/llm-config'],
  supervision:    ['/admin/policy', '/admin/traces', '/admin/supervisor', '/admin/vision'],
  integrations:   ['/admin/demo-pmsi'],
};

// ── Métadonnées familles ──────────────────────────────────────────────────────

export const FAMILIES_META: FamilyMeta[] = [
  { id: 'clinique',       label: 'Clinique',       icon: 'bi-heart-pulse', landing: '/home',          profils: null },
  { id: 'administration', label: 'Administration', icon: 'bi-gear-fill',   landing: '/admin/users',   profils: [ProfilEnum.ADMIN] },
  { id: 'supervision',    label: 'Supervision',    icon: 'bi-activity',    landing: '/admin/policy',  profils: [ProfilEnum.CADRE, ProfilEnum.ADMIN] },
  { id: 'integrations',   label: 'Intégrations',   icon: 'bi-plug-fill',   landing: '/admin/demo-pmsi', profils: [ProfilEnum.ADMIN] },
];

export const FAMILIES_ORDER: FamilyId[] = ['clinique', 'administration', 'supervision', 'integrations'];

// ── Items sidebar par famille ─────────────────────────────────────────────────

export const SIDEBAR_ITEMS: Record<FamilyId, SidebarItem[]> = {
  clinique: [
    { path: '/home',            label: 'Accueil',    icon: 'bi-house-fill',  profils: null },
    { path: '/patients',        label: 'Patients',   icon: 'bi-people-fill', profils: null },
  ],
  administration: [
    { path: '/admin/users',      label: 'Utilisateurs', icon: 'bi-people-fill',  profils: [ProfilEnum.ADMIN] },
    { path: '/admin/patients',   label: 'Patients',     icon: 'bi-person-vcard', profils: [ProfilEnum.ADMIN] },
    { path: '/admin/llm-config', label: 'Config LLM',   icon: 'bi-cpu',          profils: [ProfilEnum.ADMIN] },
  ],
  supervision: [
    { path: '/admin/policy',     label: 'Autonomie',    icon: 'bi-shield-check',   profils: [ProfilEnum.CADRE, ProfilEnum.ADMIN] },
    { path: '/admin/traces',     label: 'Traces AI',    icon: 'bi-journal-text',   profils: [ProfilEnum.CADRE, ProfilEnum.ADMIN] },
    { path: '/admin/supervisor', label: 'Supervisor',   icon: 'bi-bar-chart-line', profils: [ProfilEnum.CADRE, ProfilEnum.ADMIN] },
    { path: '/admin/vision',     label: 'Vision / RPA', icon: 'bi-eye',            profils: [ProfilEnum.CADRE, ProfilEnum.ADMIN] },
  ],
  integrations: [
    { path: '/admin/demo-pmsi', label: 'PMSI Démo',       icon: 'bi-clipboard2-pulse', profils: [ProfilEnum.ADMIN] },
  ],
};

// Pages des plugins à dépôt propre (`@/shared/plugins`) : rangées dans leur famille.
for (const { family, ...item } of PLUGIN_PAGES) {
  FAMILY_PATHS[family].push(item.path);
  SIDEBAR_ITEMS[family].push(item);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function getActiveFamily(pathname: string): FamilyId | null {
  for (const id of FAMILIES_ORDER) {
    const paths = FAMILY_PATHS[id];
    if (paths.some(p => pathname === p || pathname.startsWith(p + '/'))) {
      return id;
    }
  }
  return null;
}

export function canAccess(profils: string[] | null, userProfil: string | undefined): boolean {
  if (!profils) return true;
  return !!userProfil && profils.includes(userProfil);
}
